import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useNotify } from "../../../context/NotificationContext";
import {
  Card,
  Form,
  Input,
  Select,
  Button,
  Space,
  Row,
  Col,
  Typography,
  Badge,
  Upload,
} from "antd";
import { SaveOutlined, ReloadOutlined, ArrowLeftOutlined, GlobalOutlined, PhoneOutlined, UploadOutlined } from "@ant-design/icons";
import ImgCrop from "antd-img-crop";
import masterService from "../../../services/masterService";
import { noWhitespaceValidator, getImageUrl } from "../../../utils/commonFunctions";
import { useCommon } from "../../../context/CommonContext";

const { Title, Text } = Typography;
const { Option } = Select;

// The fixed size every uploaded flag must have — shown to the admin on the
// upload field and enforced before the file is sent. Matches the w40 flag
// raster size used across the apps.
const FLAG_IMAGE_WIDTH = 40;
const FLAG_IMAGE_HEIGHT = 20;

const AddCountry = () => {
  const navigate = useNavigate();
  const { setPageTitle } = useCommon();
  const notify = useNotify();
  const [form] = Form.useForm();

  const [loading, setLoading] = useState(false);
  const [currencies, setCurrencies] = useState([]);
  // Current value of the hidden `flag` field, for the preview thumbnail.
  const flagValue = Form.useWatch("flag", form);

  // Flag image upload — same flow as the movie edit page: the file goes to
  // /admin/upload/image (Hetzner S3) and the returned s3_key is what the
  // form submits in `flag`. getImageUrl() turns the key back into a URL.
  const onFlagUpload = async (file) => {
    if (!file?.type?.startsWith("image/")) {
      notify.error({ message: "You can only upload image files!" });
      return;
    }
    const isLt10M = file.size / 1024 / 1024 < 10;
    if (!isLt10M) {
      notify.error({ message: "Image must be smaller than 10MB!" });
      return;
    }
    try {
      const formData = new FormData();
      formData.append("image_type", "images");
      formData.append("folder_name", "flags");
      formData.append("image", file);
      const response = await masterService.uploadImage(formData);
      const s3Key = response?.data?.data?.s3_key;
      if (s3Key) {
        form.setFieldsValue({ flag: s3Key });
        notify.success({ message: "Flag image uploaded successfully" });
      } else {
        notify.error({ message: response?.data?.message || "Failed to upload flag image" });
      }
    } catch (error) {
      notify.error({
        message: error?.response?.data?.message || "Failed to upload flag image. Please try again.",
      });
    }
  };

  const fetchCurrencies = useCallback(async () => {
    try {
      const response = await masterService.getCurrencies({
        perPage: 1000,
        currentPage: 1,
      });

      if (response.data && response.data.status) {
        const currenciesData =
          response.data.data?.data || response.data.data || [];
        setCurrencies(currenciesData);
      } else {
        notify.error({ message: "Failed to fetch currencies" });
      }
    } catch (error) {
      console.error("Error fetching currencies:", error);
      notify.error({ message: "Error fetching currencies" });
    }
  }, [notify]);

  const handleSubmit = async (values) => {
    setLoading(true);
    try {
      const response = await masterService.addCountry(values);

      if (response.data && response.data.status) {
        notify.success({
          message: response.data.message || "Country added successfully",
        });
        form.resetFields();
        navigate("/countries");
      } else {
        notify.error({
          message: response.data.message || "Failed to add country",
        });
      }
    } catch (error) {
      console.error("Error adding country:", error);
      notify.error({ message: "Error adding country" });
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    form.resetFields();
  };

  const handleGoBack = () => {
    navigate("/countries");
  };

  useEffect(() => {
    setPageTitle("Add Country");
    fetchCurrencies();
  }, [fetchCurrencies, setPageTitle]);

  return (
    <div className="min-h-screen">
      <Card
        className="shadow-lg border-0"
        bodyStyle={{
          padding: "32px",
          backgroundColor: "#ffffff",
          borderColor: "#d9d9d9",
        }}
      >
        <Form
          form={form}
          layout="vertical"
          size="large"
          onFinish={handleSubmit}
          initialValues={{
            country_is_active: "Y",
            country_mob_code_visible: false,
          }}
          className="space-y-6"
        >
          {/* Basic Information Section */}
          <div
            className="bg-white p-6 rounded-lg border border-gray-200"
            style={{ backgroundColor: "#ffffff", borderColor: "#d9d9d9" }}
          >
            <div className="flex items-center gap-2 mb-4">
              <GlobalOutlined />
              <Title
                level={4}
                className="!mb-0"
                style={{ color: "rgba(0, 0, 0, 0.88)" }}
              >
                Country Information
              </Title>
            </div>

            <Row gutter={[24, 16]}>
              <Col xs={24} sm={12} lg={8}>
                <Form.Item
                  label={
                    <span
                      className="font-semibold"
                      style={{ color: "rgba(0, 0, 0, 0.88)" }}
                    >
                      Country Name
                    </span>
                  }
                  name="country_name"
                  rules={[
                    { required: true, message: "Please enter country name" },
                    {
                      pattern: /^[a-zA-Z ]+$/,
                      message: "Country name should only contain letters and spaces",
                    },
                    {
                      validator: noWhitespaceValidator,
                    },
                  ]}
                >
                  <Input
                    placeholder="Enter country name"
                    className="rounded-lg"
                  />
                </Form.Item>
              </Col>

              <Col xs={24} sm={12} lg={8}>
                <Form.Item
                  label={
                    <span
                      className="font-semibold"
                      style={{ color: "rgba(0, 0, 0, 0.88)" }}
                    >
                      ISO Code
                    </span>
                  }
                  name="iso_code"
                  rules={[
                    { required: true, message: "Please enter ISO code" }
                   
                  ]}
                >
                  <Input
                    placeholder="Enter ISO code (e.g., US, IN)"
                    className="rounded-lg"
                    style={{ textTransform: "uppercase" }}
                  />
                </Form.Item>
              </Col>

              <Col xs={24} sm={12} lg={8}>
                <Form.Item
                  label={
                    <span
                      className="font-semibold"
                      style={{ color: "rgba(0, 0, 0, 0.88)" }}
                    >
                      Status
                    </span>
                  }
                  name="country_is_active"
                >
                  <Select className="rounded-lg">
                    <Option value="Y">
                      <Badge status="success" text="Active" />
                    </Option>
                    <Option value="N">
                      <Badge status="default" text="Inactive" />
                    </Option>
                  </Select>
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={[24, 16]}>
              <Col xs={24} sm={12} lg={8}>
                <Form.Item
                  label={
                    <span
                      className="font-semibold"
                      style={{ color: "rgba(0, 0, 0, 0.88)" }}
                    >
                      Currency
                    </span>
                  }
                  name="curr_id"
                  rules={[{ required: true, message: "Please select currency" }]}
                >
                  <Select
                    placeholder="Select currency"
                    className="rounded-lg"
                    showSearch
                    optionFilterProp="children"
                    filterOption={(input, option) =>
                      (option?.children ?? "")
                        .toLowerCase()
                        .includes(input.toLowerCase())
                    }
                  >
                    {currencies.map((currency) => (
                      <Option
                        key={currency.curr_id}
                        value={currency.curr_id}
                      >
                        {currency.curr_name
                          ? `${currency.curr_name} (${currency.curr_code})`
                          : currency.curr_code}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
            </Row>
          </div>

          {/* Code Configuration Section */}
          <div
            className="bg-white p-6 rounded-lg border border-gray-200"
            style={{ backgroundColor: "#ffffff", borderColor: "#d9d9d9" }}
          >
            <div className="flex items-center gap-2 mb-4">
              <PhoneOutlined />
              <Title
                level={4}
                className="!mb-0"
                style={{ color: "rgba(0, 0, 0, 0.88)" }}
              >
                Code Configuration
              </Title>
            </div>

            <Row gutter={[24, 16]}>
              <Col xs={24} sm={12} lg={8}>
                <Form.Item
                  label={
                    <span
                      className="font-semibold"
                      style={{ color: "rgba(0, 0, 0, 0.88)" }}
                    >
                      Country Code
                    </span>
                  }
                  name="country_code"
                  rules={[
                    { required: true, message: "Please enter country code" },
                  ]}
                >
                  <Input
                    placeholder="Enter country code (e.g., +1, +91)"
                    className="rounded-lg"
                  />
                </Form.Item>
              </Col>

              <Col xs={24} sm={12} lg={8}>
                <Form.Item
                  label={
                    <span
                      className="font-semibold"
                      style={{ color: "rgba(0, 0, 0, 0.88)" }}
                    >
                      Mobile Code
                    </span>
                  }
                  name="country_mob_code"
                  rules={[
                    { required: true, message: "Please enter country mobile code" },
                    {
                      pattern: /^\+[0-9]{1,3}$/,
                      message: "Mobile code should start with + and be 1-3 digits",
                    },
                  ]}
                >
                  <Input
                    placeholder="Enter mobile code (e.g., +1, +91)"
                    className="rounded-lg"
                  />
                </Form.Item>
              </Col>

              <Col xs={24} sm={12} lg={8}>
                <Form.Item
                  label={
                    <span
                      className="font-semibold"
                      style={{ color: "rgba(0, 0, 0, 0.88)" }}
                    >
                      Mobile Country Visible
                    </span>
                  }
                  name="country_mob_code_visible"
                  dependencies={["country_mob_code"]}
                  rules={[
                    ({ getFieldValue }) => ({
                      validator(_, value) {
                        const mobCode = String(
                          getFieldValue("country_mob_code") || ""
                        ).trim();
                        if (value === true && !mobCode) {
                          return Promise.reject(
                            new Error("Enter a mobile code before making it visible")
                          );
                        }
                        return Promise.resolve();
                      },
                    }),
                  ]}
                >
                  <Select className="rounded-lg">
                    <Option value={true}>
                      <Badge status="success" text="Visible" />
                    </Option>
                    <Option value={false}>
                      <Badge status="default" text="Hidden" />
                    </Option>
                  </Select>
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={[24, 16]}>
              <Col xs={24} sm={12} lg={8}>
                {/* The uploaded file's s3_key travels in this hidden field. */}
                <Form.Item name="flag" hidden>
                  <Input />
                </Form.Item>
                <Form.Item
                  label={
                    <span
                      className="font-semibold"
                      style={{ color: "rgba(0, 0, 0, 0.88)" }}
                    >
                      Flag Image
                    </span>
                  }
                >
                  {flagValue && (
                    <div
                      style={{
                        marginBottom: 8,
                        display: "flex",
                        alignItems: "center",
                        gap: 12,
                      }}
                    >
                      <img
                        src={getImageUrl(flagValue)}
                        alt="Country flag"
                        height={24}
                        style={{ border: "1px solid #d9d9d9", borderRadius: 2 }}
                      />
                      <Button
                        type="link"
                        danger
                        size="small"
                        onClick={() => form.setFieldsValue({ flag: null })}
                      >
                        Remove
                      </Button>
                    </div>
                  )}
                  {/* Same pattern as the movie edit page's schedule images: any
                      image is accepted and the admin crops it to the flag's
                      fixed aspect in the modal — nothing gets rejected. */}
                  <ImgCrop
                    aspect={FLAG_IMAGE_WIDTH / FLAG_IMAGE_HEIGHT}
                    rotationSlider
                    quality={1}
                  >
                    <Upload
                      accept="image/*"
                      showUploadList={false}
                      beforeUpload={(file) => {
                        onFlagUpload(file);
                        return false;
                      }}
                    >
                      <Button icon={<UploadOutlined />}>
                        {flagValue ? "Change Flag Image" : "Upload Flag Image"}
                      </Button>
                    </Upload>
                  </ImgCrop>
                  <Text
                    type="secondary"
                    style={{ fontSize: 12, display: "block", marginTop: 6 }}
                  >
                    Image is cropped to the flag size ({FLAG_IMAGE_WIDTH} × {FLAG_IMAGE_HEIGHT}) · max 10MB
                  </Text>
                </Form.Item>
              </Col>
            </Row>
          </div>

          {/* Action Buttons */}
          <div
            className="flex justify-end gap-4 pt-6 border-t"
            style={{ borderColor: "#d9d9d9" }}
          >
            <Space size="middle">
              <Button
                onClick={handleReset}
                icon={<ReloadOutlined />}
                size="large"
                className="px-6"
              >
                Reset
              </Button>
              <Button
                onClick={handleGoBack}
                icon={<ArrowLeftOutlined />}
                size="large"
                className="px-6"
              >
                Cancel
              </Button>
              <Button
                type="primary"
                htmlType="submit"
                loading={loading}
                icon={<SaveOutlined />}
                size="large"
                className="px-8"
              >
                {loading ? "Adding..." : "Add Country"}
              </Button>
            </Space>
          </div>
        </Form>
      </Card>
    </div>
  );
};

export default AddCountry;
